// Bagian OTP WhatsApp yang dipakai login 2FA, lupa password, dan pencairan: input kode 6 digit dan tombol
// kirim ulang yang aktif mulai `resendAvailableAt` dari respons backend.
import { useEffect, useState, type InputHTMLAttributes } from 'react';

import { Field } from './ui';

export const OTP_LENGTH = 6;

const expiryTime = new Intl.DateTimeFormat('id-ID', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Jakarta',
});

/** Untuk OTP yang responsnya tanpa `resendAvailableAt`: kirim ulang aktif `ms` dari sekarang. */
export const resendAvailableAfter = (ms: number): string => new Date(Date.now() + ms).toISOString();

/** "Kode berlaku sampai 14.05 WIB" dari `otpExpiresAt` respons backend. */
export const otpExpiryText = (otpExpiresAt: string): string =>
  `Kode berlaku sampai ${expiryTime.format(new Date(otpExpiresAt))} WIB.`;

export function OtpCodeField({
  value,
  onValueChange,
  error,
  label = 'Kode OTP',
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (code: string) => void;
  error?: string | undefined;
  label?: string;
}) {
  return (
    <Field
      label={label}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={OTP_LENGTH}
      placeholder="6 digit"
      value={value}
      onChange={(e) => onValueChange(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
      error={error}
      {...rest}
    />
  );
}

/** Detik tersisa sampai `availableAt` (ISO); 0 bila sudah lewat. */
function useSecondsUntil(availableAt: string | null): number {
  const target = availableAt ? Date.parse(availableAt) : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (target <= Date.now()) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [target]);
  return Math.max(0, Math.ceil((target - now) / 1000));
}

export function ResendOtpButton({
  availableAt,
  loading,
  onResend,
}: {
  availableAt: string | null;
  loading: boolean;
  onResend: () => void;
}) {
  const seconds = useSecondsUntil(availableAt);
  const waiting = seconds > 0;
  return (
    <p className="text-center text-xs text-slate-500">
      Belum menerima kode?{' '}
      <button
        type="button"
        className="font-semibold text-fola-indigo hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
        disabled={waiting || loading}
        onClick={onResend}
      >
        {loading ? 'Mengirim…' : waiting ? `Kirim ulang dalam ${seconds} detik` : 'Kirim ulang'}
      </button>
    </p>
  );
}
